/**
 * Product Card Add to Cart Functionality
 * Compatible with SuperCart and other cart drawer apps
 */

(function () {
  'use strict';

  function shouldOpenVariantModal(button) {
    return button?.dataset?.hasVariants === 'true';
  }

  function resolveQuickBuyModal(button) {
    if (!button) return null;

    let modalId = button.getAttribute('aria-controls') || button.dataset.modalId;
    let modal = modalId ? document.getElementById(modalId) : null;

    const card = button.closest('[data-quick-buy-modal-id]');
    const cardModalId = card?.getAttribute('data-quick-buy-modal-id');

    if (!modal && cardModalId) {
      modalId = cardModalId;
      modal = document.getElementById(modalId);
    }

    if (modal) {
      return modal;
    }

    const productHandle =
      button.dataset.productHandle || card?.getAttribute('data-product-handle');

    if (!productHandle) {
      return null;
    }

    modalId =
      modalId ||
      cardModalId ||
      `quick-buy-${productHandle}-${Math.random().toString(36).slice(2)}`;

    modal = document.createElement('quick-buy-modal');
    modal.id = modalId;
    modal.setAttribute('handle', productHandle);
    modal.className = 'modal modal--lg';

    if (card) {
      card.setAttribute('data-quick-buy-modal-id', modalId);
      card.appendChild(modal);
    } else {
      document.body.appendChild(modal);
    }

    if (!button.getAttribute('aria-controls')) {
      button.setAttribute('aria-controls', modalId);
    }

    return modal;
  }

  function openVariantModal(button) {
    const modal = resolveQuickBuyModal(button);
    if (modal && typeof modal.show === 'function') {
      modal.show();
      return true;
    }

    return false;
  }

  // Add to Cart Handler - Works with Super Cart and other cart apps
  async function addToCart(button) {
    if (shouldOpenVariantModal(button) && openVariantModal(button)) {
      return;
    }

    const variantId = button.dataset.variantId || button.dataset.variant;

    if (!variantId || button.disabled) {
      return;
    }

    button.disabled = true;
    button.classList.add('loading');
    const originalText = button.textContent;

    try {
      // Standard Shopify add to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: parseInt(variantId),
          quantity: 1,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to add to cart');
      }

      const item = await response.json();

      // Dispatch cart change event for theme compatibility
      document.dispatchEvent(
        new CustomEvent('cart:change', {
          detail: { variant: item, quantity: 1 },
        })
      );

      // Check for various cart apps and trigger them (with error handling)
      try {
        if (window.SUPERCART || document.querySelector('.supercart-drawer')) {
          // SuperCart integration
          if (window.SUPERCART && window.SUPERCART.api) {
            window.SUPERCART.api.toggleRightDrawer();
          } else {
            // Fallback: trigger click on Super Cart button
            const superCartTrigger = document.querySelector(
              '[data-supercart-toggle], .supercart-trigger, #supercart-toggle'
            );
            if (superCartTrigger) {
              superCartTrigger.click();
            }
          }
        } else if (window.CartDrawer) {
          // CartDrawer app integration
          window.CartDrawer.open();
        } else if (window.slideCart) {
          // Slide Cart integration
          window.slideCart.open();
        } else {
          // Fallback to standard Shopify cart drawer
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            // Try different methods to open the cart drawer
            if (typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            } else if (cartDrawer.showModal) {
              cartDrawer.showModal();
            } else if (cartDrawer.setAttribute) {
              cartDrawer.setAttribute('open', '');
            } else {
              // Try clicking a cart icon as fallback
              const cartIcon = document.querySelector(
                '[data-drawer-open="drawer-cart"], .header__cart-icon, .cart-icon'
              );
              if (cartIcon) {
                cartIcon.click();
              }
            }
          } else {
            // Try to find and click cart trigger buttons
            const cartTriggers = document.querySelectorAll(
              '[data-drawer-open="drawer-cart"], .header__cart-icon, .cart-icon, a[href="/cart"]'
            );
            if (cartTriggers.length > 0) {
              // Click the first cart trigger that's not a count element
              for (const trigger of cartTriggers) {
                if (!trigger.querySelector('.cart-count, [data-cart-count]')) {
                  trigger.click();
                  break;
                }
              }
            }
          }
        }
      } catch (cartError) {
        console.error('Cart drawer error:', cartError);
        // Fallback: try to navigate to cart page
        window.location.href = '/cart';
      }

      // Update cart count across various selectors
      updateCartCount();

      // Success feedback
      if (button.classList.contains('pc-atc-button') || button.classList.contains('product-atc-btn')) {
        button.textContent = 'ADDED!';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
          button.classList.remove('loading');
        }, 2000);
      } else {
        // Quick add button - just show brief success
        button.disabled = false;
        button.classList.remove('loading');
      }
    } catch (error) {
      console.error('Add to cart failed:', error);

      // Error feedback
      if (button.classList.contains('pc-atc-button') || button.classList.contains('product-atc-btn')) {
        button.textContent = 'ERROR';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
          button.classList.remove('loading');
        }, 2000);
      } else {
        button.disabled = false;
        button.classList.remove('loading');
      }

      // Show error message if available
      if (error.message) {
        // Try to show error in existing notification system
        const errorEl = document.createElement('div');
        errorEl.textContent = error.message;
        errorEl.style.cssText =
          'position:fixed;top:20px;right:20px;background:#ff4444;color:white;padding:10px;border-radius:4px;z-index:9999;';
        document.body.appendChild(errorEl);
        setTimeout(() => errorEl.remove(), 3000);
      }
    }
  }

  // Update cart count helper function
  function updateCartCount() {
    fetch('/cart.js')
      .then((res) => res.json())
      .then((cart) => {
        // Update various cart count selectors
        const selectors = [
          '.header__cart-count',
          '.cart-count',
          '.supercart-quantity',
          '[data-cart-count]',
          '.cart-item-count',
          '.header-cart-count',
        ];

        selectors.forEach((selector) => {
          document.querySelectorAll(selector).forEach((el) => {
            el.textContent = cart.item_count;
            el.setAttribute('data-count', cart.item_count);
          });
        });

        // Dispatch cart refresh event
        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            detail: { cart: cart },
          })
        );
      })
      .catch((error) => console.error('Failed to update cart count:', error));
  }

  // Product card gallery functionality
  function initializeGallery(gallery) {
    if (gallery.dataset.initialized === 'true') return;

    const track = gallery.querySelector('.pc-track');
    const dots = gallery.querySelectorAll('.pc-dot');

    if (!track || dots.length === 0) return;

    let currentIndex = 0;

    dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Update active dot
        dots.forEach((d) => d.classList.remove('is-active'));
        dot.classList.add('is-active');

        // Scroll to image
        const scrollLeft = index * track.offsetWidth;
        track.scrollTo({ left: scrollLeft, behavior: 'smooth' });
        currentIndex = index;
      });
    });

    gallery.dataset.initialized = 'true';
  }

  // Initialize everything when DOM is loaded
  function init() {
    // Initialize galleries
    document.querySelectorAll('.pc-gallery').forEach(initializeGallery);

    // Event delegation for add to cart buttons
    document.addEventListener('click', function (e) {
      // Quick Add buttons - support both class names
      const quickAddBtn = e.target.closest('.quick-add-btn') || e.target.closest('.pc-quick-add');
      if (quickAddBtn) {
        e.preventDefault();
        e.stopPropagation();
        addToCart(quickAddBtn);
        return;
      }

      // ATC buttons - support both class names
      const atcBtn = e.target.closest('.product-atc-btn') || e.target.closest('.pc-atc-button');
      if (atcBtn) {
        e.preventDefault();
        e.stopPropagation();
        addToCart(atcBtn);
        return;
      }
    });

    // Re-initialize galleries when new content is loaded (for dynamic content)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Element node
            // Check if the node itself is a gallery
            if (node.classList && node.classList.contains('pc-gallery')) {
              initializeGallery(node);
            }
            // Check for galleries within the node
            node.querySelectorAll && node.querySelectorAll('.pc-gallery').forEach(initializeGallery);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
